import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI, contactsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { Shield, Plus, Pencil, Trash2, UserCog, Users, Bug, ChevronRight, Check, X } from 'lucide-react';
import { bugReportsAPI } from '../lib/api';
import { PASSWORD_RULES, validatePassword } from '../lib/passwordValidation';

const AdminPanel = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bugCount, setBugCount] = useState(0);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
  });
  const [allContacts, setAllContacts] = useState([]);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    loadUsers();
    loadBugCount();
    loadContacts();
  }, [isAdmin, navigate]);

  const loadContacts = async () => {
    try {
      const response = await contactsAPI.list({ limit: 1000 });
      setAllContacts(response.data?.data || response.data || []);
    } catch (error) {
      console.error('Failed to load contacts');
    }
  };

  const loadUsers = async () => {
    try {
      const response = await adminAPI.listUsers();
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadBugCount = async () => {
    try {
      const response = await bugReportsAPI.getCount();
      setBugCount(response.data.count);
    } catch (error) {
      console.error('Failed to load bug count:', error);
    }
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.email || !formData.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    const { valid, failures } = validatePassword(formData.password);
    if (!valid) {
      toast.error(failures[0].message);
      return;
    }

    try {
      await adminAPI.createUser(formData);
      toast.success('User created successfully');
      setShowCreateDialog(false);
      resetForm();
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleEdit = async () => {
    if (!formData.name || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const updateData = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        is_active: formData.is_active,
        linked_contact_id: formData.linked_contact_id || null,
      };
      if (formData.password) {
        const { valid, failures } = validatePassword(formData.password);
        if (!valid) {
          toast.error(failures[0].message);
          return;
        }
        updateData.password = formData.password;
      }
      
      await adminAPI.updateUser(selectedUser.id, updateData);
      toast.success('User updated successfully');
      setShowEditDialog(false);
      resetForm();
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      await adminAPI.deleteUser(deleteId);
      toast.success('User deleted successfully');
      setDeleteId(null);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openEditDialog = (userToEdit) => {
    setSelectedUser(userToEdit);
    setFormData({
      name: userToEdit.name,
      email: userToEdit.email,
      password: '',
      role: userToEdit.role,
      is_active: userToEdit.is_active !== false,
      linked_contact_id: userToEdit.linked_contact_id || '',
    });
    setShowEditDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'user',
    });
    setSelectedUser(null);
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="space-y-6" data-testid="admin-panel">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-3">
            <Shield className="h-8 w-8 text-yellow-400" />
            Admin Panel
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage users and system settings
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="add-user-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* Bug Reports Card */}
      <Card 
        className="border-border/50 bg-card/50 cursor-pointer hover:bg-card/80 transition-colors"
        onClick={() => navigate('/admin/bugs')}
        data-testid="bug-reports-card"
      >
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-red-500/20 flex items-center justify-center">
                <Bug className="h-6 w-6 text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Bug Reports</h3>
                <p className="text-sm text-muted-foreground">
                  {bugCount > 0 
                    ? `${bugCount} open bug${bugCount !== 1 ? 's' : ''} need${bugCount === 1 ? 's' : ''} attention`
                    : 'No open bugs'
                  }
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {bugCount > 0 && (
                <Badge variant="outline" className="bg-red-500/20 text-red-400 border-red-500/30">
                  {bugCount} open
                </Badge>
              )}
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="border-border/50 bg-card/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-cyan-400" />
            User Management
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
            </div>
          ) : users.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow className="border-border/50">
                  <TableHead className="data-table">Name</TableHead>
                  <TableHead className="data-table">Email</TableHead>
                  <TableHead className="data-table">Role</TableHead>
                  <TableHead className="data-table">Linked Contact</TableHead>
                  <TableHead className="data-table">Status</TableHead>
                  <TableHead className="data-table">Last Login</TableHead>
                  <TableHead className="data-table">Created</TableHead>
                  <TableHead className="data-table text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow
                    key={u.id}
                    className="border-border/50 hover:bg-muted/30"
                    data-testid={`user-row-${u.id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{u.name}</p>
                        {u.id === user?.id && (
                          <Badge variant="outline" className="text-xs border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
                            You
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {u.email}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          u.role === 'admin'
                            ? 'border-yellow-500/30 bg-yellow-500/10 text-yellow-400'
                            : u.role === 'viewer'
                            ? 'border-blue-500/30 bg-blue-500/10 text-blue-400'
                            : 'border-zinc-500/30 bg-zinc-500/10 text-zinc-400'
                        }
                        data-testid={`user-role-${u.id}`}
                      >
                        {u.role === 'admin' ? (
                          <><Shield className="h-3 w-3 mr-1" /> Admin</>
                        ) : u.role === 'viewer' ? (
                          'View Only'
                        ) : (
                          'User'
                        )}
                      </Badge>
                      {u.is_active === false && (
                        <Badge variant="outline" className="border-red-500/30 bg-red-500/10 text-red-400 ml-1">
                          Inactive
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {u.linked_contact_name || <span className="opacity-40">-</span>}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          u.is_active === false
                            ? 'border-red-500/30 bg-red-500/10 text-red-400'
                            : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
                        }
                      >
                        {u.is_active === false ? 'Inactive' : 'Active'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {u.last_login ? formatDistanceToNow(new Date(u.last_login), { addSuffix: true }) : <span className="opacity-40">Never</span>}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDistanceToNow(new Date(u.created_at), { addSuffix: true })}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(u)}
                          data-testid={`edit-user-${u.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteId(u.id)}
                          disabled={u.id === user?.id}
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <UserCog className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No users found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => { setShowCreateDialog(open); if (!open) resetForm(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>
              Add a new user to the system
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="create-name">Full Name *</Label>
              <Input
                id="create-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter name"
                data-testid="create-user-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-email">Email *</Label>
              <Input
                id="create-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter email"
                data-testid="create-user-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-password">Password *</Label>
              <Input
                id="create-password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Enter password"
                data-testid="create-user-password"
              />
              {formData.password && (
                <ul className="text-xs space-y-0.5 mt-1">
                  {PASSWORD_RULES.map((rule, i) => {
                    const pass = rule.test(formData.password);
                    return (
                      <li key={i} className={`flex items-center gap-1 ${pass ? 'text-emerald-400' : 'text-muted-foreground'}`}>
                        {pass ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                        {rule.message}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-role">Role</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger data-testid="create-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="viewer">View Only</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleCreate} data-testid="confirm-create-user">
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(open) => { setShowEditDialog(open); if (!open) resetForm(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user information
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Full Name *</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter name"
                data-testid="edit-user-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email">Email *</Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter email"
                data-testid="edit-user-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-password">New Password (leave blank to keep current)</Label>
              <Input
                id="edit-password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Enter new password"
                data-testid="edit-user-password"
              />
              {formData.password && (
                <ul className="text-xs space-y-0.5 mt-1">
                  {PASSWORD_RULES.map((rule, i) => {
                    const pass = rule.test(formData.password);
                    return (
                      <li key={i} className={`flex items-center gap-1 ${pass ? 'text-emerald-400' : 'text-muted-foreground'}`}>
                        {pass ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                        {rule.message}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-role">Role</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger data-testid="edit-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="viewer">View Only</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between py-2">
              <div>
                <Label>Linked Contact</Label>
                <p className="text-xs text-muted-foreground">Links user to their contact record for personalized views</p>
              </div>
            </div>
            <Select value={formData.linked_contact_id || ''} onValueChange={(v) => setFormData({ ...formData, linked_contact_id: v === '_none' ? '' : v })}>
              <SelectTrigger data-testid="linked-contact-select">
                <SelectValue placeholder="Select a contact..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">None</SelectItem>
                {allContacts.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.contact_name}{c.organization_name ? ` (${c.organization_name})` : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center justify-between py-2">
              <div>
                <Label>Account Status</Label>
                <p className="text-xs text-muted-foreground">Inactive users cannot log in</p>
              </div>
              <Button
                variant={formData.is_active !== false ? 'default' : 'destructive'}
                size="sm"
                onClick={() => setFormData({ ...formData, is_active: formData.is_active === false ? true : false })}
                data-testid="toggle-active-btn"
              >
                {formData.is_active !== false ? 'Active' : 'Inactive'}
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditDialog(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleEdit} data-testid="confirm-edit-user">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this user? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-user"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminPanel;
